import { computed, signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindShow, html, list } from "../lib/bind";
import { formatDate } from "../lib/date";
import { formatMoney } from "../lib/money";
import { currentScope } from "../lib/scope";
import { labelRow } from "../lib/table";
import { toast } from "../lib/toast";
import { breadcrumb } from "../stores/breadcrumb";
import {
  type InvoiceImportPreview,
  type InvoiceImportRow,
  commitInvoiceImport,
  discardInvoiceImport,
  invoiceImportTemplateUrl,
  uploadInvoiceImport,
} from "../services/invoiceImport";

function rowStatus(row: InvoiceImportRow): string {
  if (row.errors.length) return row.errors.join("; ");
  if (row.is_duplicate) return "Already on record — skipped";
  if (row.is_duplicate_in_file) return "Number repeated in this file — skipped";

  // Only ever set on the row that actually causes the creation, so a run of invoices
  // sharing one new client says this once — on the first of them.
  const creates: string[] = [];
  if (row.will_create_client) creates.push("client");
  if (row.will_create_project) creates.push("project");

  const base = row.warnings.length ? row.warnings.join("; ") : "Will import";
  return creates.length ? `${base} — creates new ${creates.join(" + ")}` : base;
}

function rowClass(row: InvoiceImportRow): string {
  if (row.errors.length) return "preview-row-error";
  if (row.is_duplicate || row.is_duplicate_in_file) return "preview-row-duplicate";
  if (row.warnings.length) return "preview-row-warning";
  return "";
}

export function invoicesImportView(): Node {
  breadcrumb.value = { label: "Invoices", path: "/invoices" };

  const preview = signal<InvoiceImportPreview | null>(null);
  const busy = signal(false);
  const fileInputRef = signal<HTMLInputElement | null>(null);

  const hasPreview = computed(() => preview.value !== null);
  const hasNewNames = computed(
    () =>
      (preview.value?.new_client_names.length ?? 0) > 0 ||
      (preview.value?.new_project_names.length ?? 0) > 0,
  );

  async function doUpload(): Promise<void> {
    const input = fileInputRef.value;
    const file = input?.files?.[0];
    if (!file) {
      toast.error("Choose a filled-in .xlsx file.");
      return;
    }
    busy.value = true;
    try {
      preview.value = await uploadInvoiceImport(file);
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Upload failed");
    } finally {
      busy.value = false;
      if (input) input.value = "";
    }
  }

  async function doCommit(): Promise<void> {
    const current = preview.value;
    if (!current) return;
    busy.value = true;
    try {
      const job = await commitInvoiceImport(current.invoice_import.id);
      if (job.status === "failed") {
        toast.error(job.error ?? "Import failed");
        return;
      }
      const imported = job.payload?.["imported_count"];
      const skipped = job.payload?.["skipped_count"];
      const importedLabel = typeof imported === "number" ? String(imported) : "0";
      const skippedLabel = typeof skipped === "number" ? String(skipped) : "0";
      toast.queueSuccess(
        `Imported ${importedLabel} invoice(s); ${skippedLabel} already on record.`,
      );
      preview.value = null;
      location.assign("/invoices");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Commit failed");
    } finally {
      busy.value = false;
    }
  }

  async function doDiscard(): Promise<void> {
    const current = preview.value;
    if (!current) return;
    try {
      await discardInvoiceImport(current.invoice_import.id);
      preview.value = null;
      toast.success("Import discarded.");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Discard failed");
    }
  }

  const fragment = html`<div class="view">
    <div class="card">
      <div class="card-label">Import historical invoices</div>
      <p>
        One row per invoice, with its original number and date. Invoices arrive already issued
        and backdated — they don't consume your live invoice numbering, and nothing is emailed.
        Clients and projects named in the file are created if they don't exist yet.
      </p>
      <div class="form-actions">
        <a class="btn template-link" download>Download import template</a>
      </div>
    </div>

    <div class="card">
      <div class="card-label">Upload a filled-in file</div>
      <div class="form-row">
        <div class="form-field">
          <label for="invoice-import-file">Excel file (.xlsx)</label>
          <input id="invoice-import-file" type="file" accept=".xlsx" />
        </div>
        <div class="form-actions">
          <button class="btn btn-primary upload-btn" type="button">Upload &amp; preview</button>
        </div>
      </div>
    </div>

    <div class="card import-preview">
      <div class="card-label">Preview</div>
      <p class="preview-summary"></p>
      <div class="preview-will-create">
        <p class="preview-new-clients"></p>
        <p class="preview-new-projects"></p>
      </div>
      <div class="data-table import-preview-table">
        <table>
          <thead>
            <tr>
              <th>Row</th>
              <th>Number</th>
              <th>Issued</th>
              <th>Client</th>
              <th>Project</th>
              <th>Description</th>
              <th>Net</th>
              <th>VAT</th>
              <th>Total</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody class="preview-body"></tbody>
        </table>
      </div>
      <div class="form-actions">
        <button class="btn btn-primary commit-btn" type="button">Import these invoices</button>
        <button class="btn btn-danger discard-btn" type="button">Discard</button>
      </div>
    </div>
  </div>`;

  const templateLink = fragment.querySelector<HTMLAnchorElement>(".template-link");
  const fileInput = fragment.querySelector<HTMLInputElement>("#invoice-import-file");
  const uploadBtn = fragment.querySelector<HTMLButtonElement>(".upload-btn");
  const previewCard = fragment.querySelector<HTMLElement>(".import-preview");
  const summaryEl = fragment.querySelector<HTMLElement>(".preview-summary");
  const willCreateEl = fragment.querySelector<HTMLElement>(".preview-will-create");
  const newClientsEl = fragment.querySelector<HTMLElement>(".preview-new-clients");
  const newProjectsEl = fragment.querySelector<HTMLElement>(".preview-new-projects");
  const previewTable = fragment.querySelector<HTMLTableElement>(".import-preview-table table");
  const previewBody = fragment.querySelector<HTMLTableSectionElement>(".preview-body");
  const commitBtn = fragment.querySelector<HTMLButtonElement>(".commit-btn");
  const discardBtn = fragment.querySelector<HTMLButtonElement>(".discard-btn");

  if (
    !templateLink ||
    !fileInput ||
    !uploadBtn ||
    !previewCard ||
    !summaryEl ||
    !willCreateEl ||
    !newClientsEl ||
    !newProjectsEl ||
    !previewTable ||
    !previewBody ||
    !commitBtn ||
    !discardBtn
  ) {
    throw new Error("invoice import view markup missing expected elements");
  }

  templateLink.href = invoiceImportTemplateUrl();
  fileInputRef.value = fileInput;

  uploadBtn.addEventListener("click", () => {
    void doUpload();
  });
  commitBtn.addEventListener("click", () => {
    void doCommit();
  });
  discardBtn.addEventListener("click", () => {
    void doDiscard();
  });

  bindShow(previewCard, hasPreview);
  bindShow(willCreateEl, hasNewNames);

  currentScope().effect(() => {
    const disabled = busy.value;
    uploadBtn.disabled = disabled;
    commitBtn.disabled = disabled || (preview.value?.will_import_count ?? 0) === 0;
  });

  currentScope().effect(() => {
    const current = preview.value;
    if (!current) return;
    summaryEl.textContent =
      `${String(current.invoice_import.row_count)} rows — ` +
      `${String(current.will_import_count)} to import, ` +
      `${String(current.error_count)} with errors, ` +
      `${String(current.duplicate_count)} already on record or repeated`;
    newClientsEl.textContent = current.new_client_names.length
      ? `Will create ${String(current.new_client_names.length)} new client(s): ${current.new_client_names.join(", ")}`
      : "";
    newClientsEl.style.display = current.new_client_names.length ? "" : "none";
    newProjectsEl.textContent = current.new_project_names.length
      ? `Will create ${String(current.new_project_names.length)} new project(s): ${current.new_project_names.join(", ")}`
      : "";
    newProjectsEl.style.display = current.new_project_names.length ? "" : "none";
  });

  list(
    previewBody,
    computed(() => preview.value?.rows ?? []),
    (row) => String(row.row_number),
    (row) => {
      const element = html`<tr class="${rowClass(row)}">
        <td>${String(row.row_number)}</td>
        <td>${row.invoice_number || "—"}</td>
        <td>${row.issue_date ? formatDate(row.issue_date) : "—"}</td>
        <td>${row.client_name || "—"}</td>
        <td>${row.project_name ?? "—"}</td>
        <td>${row.description || "—"}</td>
        <td>${row.net_amount ? formatMoney(row.net_amount) : "—"}</td>
        <td>${row.derived_vat_amount ? formatMoney(row.derived_vat_amount) : "—"}</td>
        <td>${row.derived_total ? formatMoney(row.derived_total) : "—"}</td>
        <td class="preview-row-status">${rowStatus(row)}</td>
      </tr>`.firstElementChild;
      if (!element) throw new Error("import preview row template produced no element");
      return labelRow(element, previewTable);
    },
  );

  return fragment;
}
